import type {ReactElement} from 'react';
import {useAppSelector} from '@/hooks';
import {Button, IconPlacement, Type} from '@Components/button';
import {getAssetUrl} from '@Utils/s3.util';
import type {CheckoutStepProps} from '@Components/checkout-flow/checkout-flow.types';
import {CheckoutMode, CheckoutStep} from '@Components/checkout-flow/checkout-flow.types';
import {getCardPaymentOptionTitle} from '@Components/checkout-flow/checkout-flow.library';
import {PaymentOptionCard} from '@Components/checkout-flow/components/payment-option-card/payment-option-card';
import {PromoCodeInput} from '@Components/checkout-flow/components/promo-code-input/promo-code-input';
import styles from './payment-options-step.module.scss';

export function PaymentOptionsStep({request, controller}: CheckoutStepProps): ReactElement | null {
  const dialogData = useAppSelector((state) => state.checkoutFlow.dialogData);
  const hasAddresses = useAppSelector((state) => state.checkoutFlow.addresses.length > 0);

  if (dialogData === null || request.mode !== CheckoutMode.DOWNLOAD) {
    return null;
  }

  const onPayWithCard = (): void => {
    controller.goToStep(CheckoutStep.STRIPE_EMBEDDED);
  };

  const onPayWithPaypal = (): void => {
    controller.goToStep(hasAddresses ? CheckoutStep.BILLING_ADDRESS_LIST : CheckoutStep.BILLING_ADDRESS_FORM);
  };

  const getPaypalWarning = (): {text: string; tooltip: string} | null => {
    if (!dialogData.isCurrencyBlockedForPaypal) {
      return null;
    }
    return {
      text: window.i18next.t('pmwjs_payments_in_x_are_not_supported', {currency: dialogData.currencyCode}),
      tooltip: window.i18next.t('pmwjs_you_will_be_charged_in_usd_instead'),
    };
  };

  return (
    <div className={styles.container}>
      <div className={styles.options}>
        <PaymentOptionCard
          imageUrl={getAssetUrl('checkout-credit-card.png')}
          imageAlt=""
          title={getCardPaymentOptionTitle(dialogData)}
          imageClassName={styles.cardImage}
          cta={
            <Button
              type={Type.PRIMARY}
              text={window.i18next.t('pmwjs_credit_debit_card')}
              icon="icon-cc-generic"
              iconPlacement={IconPlacement.LEFT}
              customClasses="_full-width"
              onClick={onPayWithCard}
            />
          }
        />
        <PaymentOptionCard
          imageUrl={getAssetUrl('checkout-paypal-logo.png')}
          imageAlt=""
          title={window.i18next.t('pmwjs_pay_with_paypal')}
          imageClassName={styles.paypalImage}
          warning={getPaypalWarning()}
          cta={
            <button type="button" className={styles.paypalButton} onClick={onPayWithPaypal} aria-label={window.i18next.t('pmwjs_checkout_with_paypal')}>
              <img src={getAssetUrl('checkout-paypal.png')} alt="" className={styles.paypalButtonImage} />
            </button>
          }
        />
      </div>
      <PromoCodeInput request={request} controller={controller} />
    </div>
  );
}
